import { INSURANCE_COVERAGE_STATUS, PATIENT_STATUS } from '../../constants';
import validate from '../../utils/validate';
import { DEPENDENT_STATUS } from './constants';
import { isPatientCoverageInfoRequired } from './utils';

const MIN_AGE = 18;

const parseMaskedInput = (value) => value.replace(/[-_]/g, '');

const BASE_VALIDATIONS = {
  reasonForVisit: {
    presence: { allowEmpty: false },
  },
  notes: {
    label: 'Message',
    length: { maximum: 300 },
  },
};

const ANONYMOUS_VALIDATIONS = {
  contactEmail: {
    label: 'Email',
    presence: { allowEmpty: false },
    email: true,
    length: { maximum: 254 },
    custom: {
      validate(value, options, key, attributes) {
        const { emailValidationResult: result } = attributes;
        if (!result) {
          return null;
        }
        if (!result.formatValid) {
          return 'is not a valid email';
        }
        if (result.mailboxStatus === 'INVALID') {
          return 'is not a real email address';
        }
        return null;
      },
    },
  },
  contactMobilePhone: {
    label: 'Mobile phone',
    presence: { allowEmpty: false },
    serverErrors: { key: 'contactPhone', skipLabelPrefix: true },
    length: { minimum: 10, maximum: 20, tokenizer: parseMaskedInput },
  },
  termsAgreement: {
    checked: true,
  },
};

const DEPENDENT_VALIDATIONS = {
  contactGivenName: {
    label: 'First name',
    presence: { allowEmpty: false },
    length: { minimum: 2, maximum: 100 },
  },
  contactFamilyName: {
    label: 'Last name',
    presence: { allowEmpty: false },
    length: { minimum: 2, maximum: 100 },
  },
  contactBirthDate: {
    label: 'Birthday',
    presence: { allowEmpty: false },
    age: {
      minimum: MIN_AGE,
      tooYoung: '^You must be at least %{count} years old to schedule an appointment',
      tooOld: '^You must be under %{count} years old',
    },
  },
};

const ACCOUNT_VALIDATIONS = {
  username: {
    label: 'Login Email',
    presence: { allowEmpty: false },
    email: true,
    length: { maximum: 254 },
    serverErrors: true,
  },
  password: {
    presence: { allowEmpty: false },
    length: { minimum: 8 },
    custom: {
      validate(value) {
        if (!value) {
          return null;
        }
        if (!/[a-z]/.test(value)) {
          return 'must contain at least one lowercase character';
        }
        if (!/\d/.test(value)) {
          return 'must contain at least one number';
        }
        return null;
      },
    },
  },
};

const PROVIDER_INSURANCE_COVERAGE_VALIDATIONS = {
  'providerInsuranceCoverage.status': {
    presence: true,
    exclusion: [INSURANCE_COVERAGE_STATUS.WILL_NOT_FILE],
  },
  insuranceIssuer: {
    label: 'Payment',
    presence: true,
  },
};

const RETURNING_PATIENT_COVERAGE_VALIDATIONS = {
  insurancePhone: {
    label: 'Insurance Phone Number',
    presence: { allowEmpty: false },
    serverErrors: { key: 'patientCoverage.insurancePhone', skipLabelPrefix: true },
    length: { minimum: 10, maximum: 20, tokenizer: parseMaskedInput },
  },
  memberId: {
    label: 'Member ID',
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  group: {
    label: 'Group ID',
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  employer: {
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  subscriberGivenName: {
    label: 'First name',
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  subscriberFamilyName: {
    label: 'Last name',
    presence: { allowEmpty: false },
    length: { minimum: 2 },
  },
  subscriberBirthDate: {
    label: 'Birthday',
    presence: { allowEmpty: false },
    age: {
      minimum: MIN_AGE,
      tooYoung: '^The subscriber should be at least %{count} years old',
      tooOld: '^You must be under %{count} years old',
    },
  },
  subscriberPostalCode: {
    label: 'Postal code',
    presence: { allowEmpty: false },
    serverErrors: { key: 'patientCoverage.postalCode', skipLabelPrefix: true },
  },
};

const NEW_PATIENT_COVERAGE_VALIDATIONS = {
  ...RETURNING_PATIENT_COVERAGE_VALIDATIONS,
  subscriberSsn: {
    label: 'SSN',
    presence: { allowEmpty: false },
    serverErrors: { key: 'patientCoverage.ssn', skipLabelPrefix: true },
    length: {
      minimum: 9,
      maximum: 9,
      tokenizer: parseMaskedInput,
    },
  },
};

function createProviderInsuranceCoverageValidations(values, provider) {
  if (
    (values.office && !values.office.insuranceCoverageEnabled) ||
    (provider && provider.office && !provider.office.insuranceCoverageEnabled)
  ) {
    return {};
  }
  return PROVIDER_INSURANCE_COVERAGE_VALIDATIONS;
}

function createPatientCoverageValidations(values, provider) {
  if (!isPatientCoverageInfoRequired(provider, values.insuranceIssuer)) {
    return {};
  }
  const patientStatus =
    typeof values.patientStatus === 'object' ? values.patientStatus.id : values.patientStatus;
  return patientStatus === PATIENT_STATUS.RETURNING
    ? RETURNING_PATIENT_COVERAGE_VALIDATIONS
    : NEW_PATIENT_COVERAGE_VALIDATIONS;
}

function createPatientBirthDateFieldValidations(values, provider) {
  const { profile: { agesTreated: { min, max } = {} } = {} } = provider;
  const validations = {
    label: 'Birthday',
    presence: { allowEmpty: false },
    age: {
      tooOld: '^The patient must be under %{count} years old',
    },
  };
  if (min) {
    validations.age.minimum = min;
    validations.age.tooYoung =
      '^The patient must be at least %{count} years old to be scheduled online with this provider';
  }
  if (max) {
    validations.age.maximum = max;
    validations.age.tooOld =
      '^The patient must be under %{count} years old to be scheduled online with this provider';
  }
  return validations;
}

function createNewPatientValidations(values, provider) {
  const validations = {
    patientGivenName: {
      label: 'First name',
      presence: { allowEmpty: false },
      length: { minimum: 2 },
    },
    patientFamilyName: {
      label: 'Last name',
      presence: { allowEmpty: false },
      length: { minimum: 2 },
    },
  };
  if (values.createDependent || values.dependentStatus === DEPENDENT_STATUS.DEPENDENT) {
    validations.patientBirthDate = createPatientBirthDateFieldValidations(values, provider);
  } else {
    validations.patientBirthDate = createPatientBirthDateFieldValidations(values, provider);
    const { minimum } = validations.patientBirthDate.age;
    if (!minimum || minimum < MIN_AGE) {
      validations.patientBirthDate.age.minimum = MIN_AGE;
      validations.patientBirthDate.age.tooYoung =
        '^You must be at least %{count} years old to schedule an appointment';
    }
  }
  return validations;
}

function createAnonymousValidations(values, provider = {}) {
  if (!provider) return {};
  const { profile: { agesTreated: { max } = {} } = {} } = provider;
  let validations = {
    ...ANONYMOUS_VALIDATIONS,
    ...createNewPatientValidations(values, provider),
  };
  if (max && max < MIN_AGE) {
    validations.dependentStatus = {
      inclusion: {
        within: [DEPENDENT_STATUS.DEPENDENT],
        message: '^This provider requires the patient to be a dependent',
      },
    };
  }
  if (values.dependentStatus === DEPENDENT_STATUS.DEPENDENT) {
    validations = { ...validations, ...DEPENDENT_VALIDATIONS };
  }
  if (values.createAccount) {
    validations = { ...validations, ...ACCOUNT_VALIDATIONS };
  }
  return validations;
}

function createSelectedPatientValidations(values, provider) {
  return {
    patient: {
      presence: true,
    },
    'patient.birthDate': createPatientBirthDateFieldValidations(values, provider),
  };
}

export function createSignedInValidations(values, provider) {
  if (values.createDependent) {
    return createNewPatientValidations(values, provider);
  }
  return createSelectedPatientValidations(values, provider);
}

export default function validateAppointmentForm(values, provider = {}) {
  let validations = { ...BASE_VALIDATIONS };
  if (values.user && values.user.patient) {
    validations = { ...validations, ...createSignedInValidations(values, provider) };
  } else {
    validations = { ...validations, ...createAnonymousValidations(values, provider) };
  }
  validations = {
    ...validations,
    ...createProviderInsuranceCoverageValidations(values, provider),
    ...createPatientCoverageValidations(values, provider),
  };

  return validate(values, validations, { cleanAttributes: false });
}
